import React, { useEffect, useState } from 'react';

interface AdBlockerDetectionProps {
  onUpgradeClick: () => void;
}

const AdBlockerDetection: React.FC<AdBlockerDetectionProps> = ({ onUpgradeClick }) => {
  const [isAdBlockerDetected, setIsAdBlockerDetected] = useState(false);

  // Open the alpha upgrade page in a new tab
  const handleUpgradeClick = () => {
    window.open('/alpha-upgrade', '_blank');
  };

  useEffect(() => {
    // Simple ad blocker detection
    const detectAdBlocker = async () => {
      try {
        // Create a bait element that ad blockers typically target
        const bait = document.createElement('div');
        bait.className = 'ad-banner ad-placeholder ad-slot adsbox';
        bait.style.position = 'absolute';
        bait.style.opacity = '0';
        bait.style.height = '1px';
        bait.style.width = '1px';
        document.body.appendChild(bait);

        // Wait a moment for ad blockers to act on the bait element
        await new Promise(resolve => setTimeout(resolve, 100));

        // Check if the bait element was hidden or removed by an ad blocker
        const isBlocked = bait.offsetHeight === 0 || 
                          bait.offsetWidth === 0 || 
                          !document.body.contains(bait);
        
        setIsAdBlockerDetected(isBlocked);
        
        // Clean up
        if (document.body.contains(bait)) {
          document.body.removeChild(bait);
        }
      } catch (error) {
        console.error('Error detecting ad blocker:', error);
      }
    };

    detectAdBlocker();
  }, []);

  if (!isAdBlockerDetected) return null;

  return (
    <div className="fixed bottom-20 left-0 right-0 flex justify-center px-4 z-30 lg:left-64">
      <div className="max-w-xl w-full rounded-lg p-6 bg-amber-900/80 text-slate-100 shadow-lg border border-amber-700">
        <div className="flex flex-col items-center text-center gap-3">
          <div className="flex items-center gap-2">
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className="h-5 w-5 text-amber-400" 
              viewBox="0 0 20 20" 
              fill="currentColor"
            >
              <path 
                fillRule="evenodd" 
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" 
                clipRule="evenodd" 
              />
            </svg>
            <div className="text-lg text-amber-400 font-semibold">
              Ad Blocker Detected
            </div>
          </div>
          
          <p className="text-slate-200">
            We've detected that you're using an ad blocker. Our free tier is supported by ads.
          </p>
          <p className="text-slate-300">
            Please disable your ad blocker to continue using the free tier, or upgrade to Alpha for an ad-free experience with faster responses.
          </p>
          
          <div className="flex flex-col items-center gap-3">
            <button
              onClick={handleUpgradeClick}
              className="px-6 py-2 bg-emerald-500 text-slate-900 rounded-md hover:bg-emerald-400 transition-colors font-medium shadow-sm"
            >
              Upgrade to Alpha
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdBlockerDetection;
